import { Activity } from '@models/activity.model';
import { ActivityHeader } from '../ActivityHeader';
import { TaskName } from '../TaskName';
import { formatActivityTime } from '@utils/formatDate';
import { Card } from '@components/atoms/Card';
import Alert from '@components/atoms/Alert';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import { useTranslation } from '@hooks/useTranslation';

export const ActivityCommunication = ({
  activity,
  handleOpenTaskDetail,
}: {
  activity: Activity;
  handleOpenTaskDetail?: (taskId: string) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const projectName = activity.project?.name || tActivityCards.notAvailable;
  const documents = activity.documents || [];

  const rightLabel = (
    <div className="flex items-center gap-2">
      <ChatOutlinedIcon fontSize="small" className="text-neutral-500" />
      <p className="text-sm text-neutral-500">
        {activity.createdAt
          ? tActivityCards.communication.communicationTime.replace(
              '{time}',
              formatActivityTime(activity.createdAt),
            )
          : tActivityCards.communication.communication}
      </p>
    </div>
  );

  return (
    <Card>
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      <div className="mt-4 space-y-3">
        {/* Context Information Section */}
        <Alert>
          <div className="text-sm text-gray-700 space-y-1">
            <TaskName
              task={activity.task}
              size="sm"
              taskId={activity.taskId || activity.task?.taskId}
              onOpenTask={handleOpenTaskDetail}
            />
            <p>
              <span className="font-medium text-gray-900">{tActivityCards.projectLabel}</span>{' '}
              {projectName}
            </p>
          </div>
        </Alert>

        {/* Communication Panel */}
        <Alert variant="info" border>
          <div className="text-sm space-y-3">
            <div className="flex items-center gap-1">
              <ChatOutlinedIcon fontSize="small" className="text-blue-500" />
              <p className="text-sm text-blue-950 font-semibold">
                {tActivityCards.communication.communication}
              </p>
            </div>

            {activity.description && (
              <p className="text-gray-700">{activity.description}</p>
            )}

            {documents.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {documents.map((doc: any, index: number) => {
                  const isImage = doc.name?.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i,
                  );
                  return (
                    <div
                      key={`${doc.documentId || index}`}
                      className="group cursor-pointer hover:scale-105 transition-transform"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(doc.url, '_blank');
                      }}>
                      <div className="bg-white rounded-lg p-2 border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all">
                        {isImage ? (
                          <div className="w-full h-20 rounded overflow-hidden bg-gray-100 mb-2">
                            <img
                              src={doc.url}
                              alt={doc.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-20 rounded bg-gray-200 flex items-center justify-center mb-2">
                            <svg
                              className="w-8 h-8 text-gray-500"
                              fill="currentColor"
                              viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                        <p
                          className="text-xs text-gray-600 text-center truncate"
                          title={doc.name}>
                          {doc.name?.length > 20
                            ? `${doc.name.substring(0, 20)}...`
                            : doc.name || tActivityCards.communication.documentFallback}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Alert>
      </div>
    </Card>
  );
};
